import React, { ChangeEvent, useState, useEffect, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PageviewIcon from '@mui/icons-material/Pageview';
import SyncIcon from '@mui/icons-material/Sync';
import SellIcon from '@mui/icons-material/Sell';
import Box from '@mui/material/Box';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import useGlobalStyles from '../../styles';
import useStyles from '../../styles/location';
import {
  BacnetGatewaySettings,
  BacnetSensorSettings,
  GatewaySensorType,
  SensorAlertsPartial,
  SensorLatest,
  VisibleSubSensor,
} from '../../services/api';
import {
  filterNewSensorsInLocation,
  GatewayVisibleSubsensors,
  getAllGatewayVisibleSubsensors,
} from '../SensorConfig/helpers';
import ConfiguredSensorsList from './ConfiguredSensorsList';
import NewSensorsList from './NewSensorsList';
import { sensorTypeDetails } from '../../utils/sensorProperties';
import SensorsInfoIcon from '../../styles/icons/SensorsInfoIcon';
import { getSensorBacnetConfig } from '../../services/apiService';
import Loading from '../../components/Loading';
import BacnetSensorsList from './BacnetSensorsList';
import { getSensorsById, getSensorsByLocId } from '../../state/selectors';
import { useAppSelector } from '../../state/store';
import TagList from './Tagging/TagList';
import useSensorFilters from './useSensorFilters';

enum ViewMode {
  configured = 'configured',
  new = 'new',
  bacnet = 'bacnet',
  tags = 'tags',
}

interface LocationSensorsListProps {
  locationId: string;
  locationSensors: Array<SensorLatest> | undefined;
  sensorsLoading: boolean;
  sensorAlertsPartial: SensorAlertsPartial[];
}

function LocationSensorsList({
  locationId,
  locationSensors,
  sensorsLoading,
  sensorAlertsPartial,
}: LocationSensorsListProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const sensorsByLocId = useAppSelector(getSensorsByLocId);
  const sensorsById = useAppSelector(getSensorsById);

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.configured);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [filteringNewSensors, setFilteringNewSensors] = useState(true);
  const [allGatewayVisibleSubSensors, setAllGatewayVisibleSubSensors] = useState<
    GatewayVisibleSubsensors[] | undefined
  >(undefined);
  const [bacnetGatewaySensors, setBacnetGatewaySensors] = useState<BacnetGatewaySettings[]>([]);

  const {
    searchParam,
    selectionType,
    filteredSensors,
    availableSensorsType,
    setSearchParam,
    setSelectionType,
    setAvailableSensors,
    resetFilters,
  } = useSensorFilters(viewMode === ViewMode.new);

  // Flatten gateway sensors into a single bacnet sensors list
  const bacnetSensors = useMemo<BacnetSensorSettings[]>(
    () => bacnetGatewaySensors.flatMap((g) => g.sensors ?? []),
    [bacnetGatewaySensors]
  );

  // Bacnet sensors matched to their SensorLatest info
  const bacnetSensorsLatest = useMemo<SensorLatest[]>(
    () =>
      bacnetSensors
        .map(({ id }) => locationSensors?.find((s) => s.id === id))
        .filter((s): s is SensorLatest => s !== undefined),
    [bacnetSensors, locationSensors]
  );

  const getNewSensorsInLoc = (visibleSubsensors: GatewayVisibleSubsensors[]) => {
    filterNewSensorsInLocation(visibleSubsensors, sensorsByLocId.get(locationId) ?? []).then(
      (newSensors) => {
        if (newSensors) {
          setAvailableSensors(newSensors);
          setFilteringNewSensors(false);
        }
      }
    );
  };

  const fetchAllGatewayVisibleSubSensors = () => {
    setFilteringNewSensors(true);
    getAllGatewayVisibleSubsensors(locationId, sensorsById, sensorsByLocId)
      .then((visibleSubsensors) => {
        setAllGatewayVisibleSubSensors(visibleSubsensors);
        getNewSensorsInLoc(visibleSubsensors);
      })
      .catch(() =>
        enqueueSnackbar('Error fetching new sensors', { variant: 'error', persist: true })
      );
  };

  // Sync available sensors into the filter hook whenever the view or source data changes
  useEffect(() => {
    if (viewMode === ViewMode.bacnet) {
      setAvailableSensors(bacnetSensorsLatest);
    } else if (viewMode === ViewMode.configured && locationSensors) {
      setAvailableSensors(locationSensors);
    } else if (viewMode === ViewMode.new) {
      if (allGatewayVisibleSubSensors === undefined) {
        fetchAllGatewayVisibleSubSensors();
      } else getNewSensorsInLoc(allGatewayVisibleSubSensors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, locationSensors, bacnetSensorsLatest, setAvailableSensors]);

  const fetchBacnetGatewaySensors = async () => {
    if (!locationSensors) return;
    const gatewaySensors = locationSensors.filter((s) => s.type === GatewaySensorType.LfWiFiSensor);
    const results = await Promise.all(
      gatewaySensors.map((s) =>
        getSensorBacnetConfig(s.id)
          .then((c) => c?.gateway)
          .catch(() => undefined)
      )
    );
    setBacnetGatewaySensors(results.filter((g): g is BacnetGatewaySettings => g !== undefined));
  };

  // Fetch bacnet config when switching to bacnet mode
  useEffect(() => {
    if (viewMode === ViewMode.bacnet) {
      setBacnetGatewaySensors([]);
      fetchBacnetGatewaySensors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Reset all state when location changes
  useEffect(() => {
    resetFilters();
    if (viewMode !== ViewMode.tags) setViewMode(ViewMode.configured);
    setAllGatewayVisibleSubSensors(undefined);
    setBacnetGatewaySensors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, resetFilters]);

  const alertMsg = useMemo(() => {
    if (!filteredSensors?.length) {
      if (viewMode === ViewMode.bacnet) return 'No Bacnet enabled sensors found';
      if (searchParam || selectionType !== 'all') return 'No sensor found matching filter';
    }
    return '';
  }, [filteredSensors, viewMode, searchParam, selectionType]);

  const toggleViewMode = (mode: ViewMode) =>
    setViewMode((prev) => (prev === mode ? ViewMode.configured : mode));

  return (
    <div>
      <List>
        <ListItem>
          <Grid container>
            <Grid
              size={{ sm: 7, xs: 7 }}
              style={{ display: 'flex', justifyContent: 'space-between', alignSelf: 'center' }}
            >
              <Box sx={{ display: 'inline-flex' }}>
                <SensorsInfoIcon className={classes.icon} />
                <Typography variant="h6" style={{ marginLeft: '10px' }}>
                  {viewMode === ViewMode.new ? 'Sensors To Be Configured' : 'Configured Sensors'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ sm: 5, xs: 5 }} style={{ textAlign: 'right' }}>
              <Box sx={{ display: 'inline-flex' }}>
                <Tooltip title="Show Tags">
                  <IconButton
                    onClick={() => toggleViewMode(ViewMode.tags)}
                    data-testid="show-tags"
                    size="large"
                  >
                    <SellIcon
                      className={viewMode === ViewMode.tags ? classes.activeIcon : classes.icon}
                    />
                  </IconButton>
                </Tooltip>
                {viewMode !== ViewMode.new && (
                  <Tooltip title="Show Bacnet enabled Sensors">
                    <IconButton onClick={() => toggleViewMode(ViewMode.bacnet)} size="large">
                      <DeviceHubIcon
                        className={viewMode === ViewMode.bacnet ? classes.activeIcon : classes.icon}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {viewMode === ViewMode.new && (
                  <Tooltip title="Refresh sensors list">
                    <IconButton onClick={fetchAllGatewayVisibleSubSensors} size="large">
                      <SyncIcon className={classes.icon} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Show New Sensors">
                  <IconButton
                    onClick={() => toggleViewMode(ViewMode.new)}
                    data-testid="show-sensors"
                    size="large"
                  >
                    {viewMode === ViewMode.new ? (
                      <CancelIcon className={globalClasses.closeBtnIcon} />
                    ) : (
                      <AddCircleIcon className={globalClasses.icon} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Show filter options">
                  <IconButton
                    onClick={() => setShowFilterOptions((v) => !v)}
                    data-testid="show-filter-options"
                    size="large"
                  >
                    <PageviewIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </ListItem>

        {showFilterOptions && (
          <ListItem style={{ paddingTop: '0' }}>
            <Grid container sx={{ flexWrap: 'nowrap' }}>
              <Grid
                size={{ sm: 8, xs: 8 }}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Box sx={{ display: 'inline-flex' }}>
                  <TextField
                    placeholder={`Search by ${viewMode === ViewMode.new ? '' : 'name or '}id`}
                    onChange={(e) => setSearchParam(e.target.value)}
                    value={searchParam}
                    variant="standard"
                  />
                  {searchParam && (
                    <IconButton
                      onClick={() => setSearchParam('')}
                      style={{ alignSelf: 'center', padding: '0' }}
                      size="large"
                    >
                      <CancelIcon className={globalClasses.closeBtnIcon} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid size={{ sm: 4, xs: 4 }} style={{ textAlign: 'right' }}>
                <Select
                  labelId="Sensor type"
                  value={selectionType}
                  onChange={(e) =>
                    setSelectionType(
                      (e as ChangeEvent<HTMLTextAreaElement | HTMLInputElement>).target.value
                    )
                  }
                  className={classes.selectBox}
                  variant="standard"
                >
                  <MenuItem value="all">All</MenuItem>
                  {availableSensorsType
                    ?.sort((a, b) =>
                      sensorTypeDetails[a].brandName.localeCompare(sensorTypeDetails[b].brandName)
                    )
                    .map((sensorType) => (
                      <MenuItem key={`sensorType-${sensorType}`} value={sensorType}>
                        {`${sensorTypeDetails[sensorType].brandName} - ${sensorTypeDetails[sensorType].label}`}
                      </MenuItem>
                    ))}
                </Select>
              </Grid>
            </Grid>
          </ListItem>
        )}
      </List>

      <div style={{ padding: '5px 1rem' }}>
        {sensorsLoading ? (
          <Loading />
        ) : (
          <>
            {viewMode === ViewMode.tags && <TagList />}
            {viewMode === ViewMode.bacnet && bacnetSensors.length > 0 && (
              <BacnetSensorsList
                filteredSensors={filteredSensors as SensorLatest[]}
                bacnetSensors={bacnetSensors}
                bacnetGatewaySensors={bacnetGatewaySensors}
              />
            )}
            {viewMode === ViewMode.new && allGatewayVisibleSubSensors && (
              <NewSensorsList
                fetchingSensors={filteringNewSensors}
                allSensors={filteredSensors as VisibleSubSensor[]}
                locationId={locationId || '#'}
                allGatewaySubSensor={allGatewayVisibleSubSensors}
                refreshNewSensors={() => setAllGatewayVisibleSubSensors(undefined)}
              />
            )}
            {viewMode === ViewMode.configured && (
              <ConfiguredSensorsList
                allSensors={filteredSensors as SensorLatest[]}
                sensorAlertsPartial={sensorAlertsPartial}
              />
            )}
            {alertMsg && <Alert severity="error">{alertMsg}</Alert>}
          </>
        )}
      </div>
    </div>
  );
}

export default LocationSensorsList;
